import { useRef, useState, useEffect } from "react";
import { ArrowRight, MessageCircle } from "lucide-react";
import axios from "axios";

const API = '/api';

const FALLBACK_POSTS = [
  {
    id: "india-fta-iwi",
    title: "The India FTA Is Here. Indian Delegates Are on the Ground. Iwi Need to Be at the Table.",
    excerpt: "The India FTA is ratified and Indian delegates are travelling Aotearoa right now. The products India wants are the products Māori produce. The alignment is structural. The marae is the most powerful meeting room in the country.",
    featured_image: "/nz-india-fta.jpg",
    category: "Economic Development",
    author_name: "Dr. Tāniora Maxwell",
    comment_count: 0,
    created_at: "2026-06-07T09:00:00.000Z",
  },
  {
    id: "budget-2026",
    title: "Budget 2026: Who Benefits, Who Pays, and Where Māori Fit in a Budget Built for an Election",
    excerpt: "Nicola Willis delivered a budget that builds roads and hospitals. What it does not build is the institutional architecture to ensure Māori communities benefit equitably. The marae is not in this budget. The boardroom is.",
    featured_image: "https://images.unsplash.com/photo-1593818621143-cb4f7ad4e5d3?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "Economic Development",
    author_name: "Dr. Tāniora Maxwell",
    comment_count: 0,
    created_at: "2026-05-28T12:00:00.000Z",
  },
  {
    id: "climate-commission-2026",
    title: "The Climate Change Commission Just Told Us What We Already Know. The Question Is What We Do Next.",
    excerpt: "The Climate Change Commission has treated risks to Māori as a standalone category for the first time. The evidence is documented. The question now is whether we wait for someone else to act, or lead the response ourselves.",
    featured_image: "https://images.unsplash.com/photo-1718398892816-9aed4f6d3fa9?crop=entropy&cs=srgb&fm=jpg&q=85",
    category: "Climate Adaptation",
    author_name: "Dr. Tāniora Maxwell",
    comment_count: 0,
    created_at: "2026-05-21T09:00:00.000Z",
  },
];

export default function BlogPreview() {
  const ref = useRef(null);
  const [posts, setPosts] = useState(FALLBACK_POSTS);

  useEffect(() => {
    axios.get(`${API}/blog?limit=3`)
      .then(r => {
        if (r.data.posts && r.data.posts.length > 0) {
          setPosts(r.data.posts);
        }
      })
      .catch(() => {});
  }, []);

  return (
    <section
      id="blog"
      data-testid="blog-preview"
      className="relative py-6 sm:py-8 overflow-hidden"
    >
      <div ref={ref} className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#bb321a] mb-4">
              Insights & Perspectives
            </p>
            <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-white leading-[1.05]">
              Indigenous voices, <span className="text-[#bb321a]">global impact.</span>
            </h2>
          </div>
          <a
            href="/blog"
            data-testid="blog-view-all"
            className="hidden sm:inline-flex items-center gap-2 text-sm font-semibold text-[#bb321a] hover:underline"
          >
            View all posts <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-1">
          {posts.map((post, i) => (
            <a
              key={post.id}
              href={`/blog/${post.id}`}
              data-testid={`blog-card-${i}`}
              className="group bg-white/5 border border-white/10 hover:border-[#bb321a]/30 transition-all duration-300 overflow-hidden"
            >
              {post.featured_image && (
                <div className="h-48 overflow-hidden">
                  <img loading="lazy"
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-xs font-semibold tracking-wider uppercase text-[#bb321a]">{post.category}</span>
                  <span className="text-xs text-white/80">{new Date(post.created_at).toLocaleDateString("en-NZ", { day: "numeric", month: "short", year: "numeric" })}</span>
                </div>
                <h3 className="font-['Syne'] text-lg font-bold text-white mb-2 group-hover:text-[#bb321a] transition-colors">
                  {post.title}
                </h3>
                <p className="text-white/80 text-sm leading-relaxed line-clamp-3 mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-white/80">By {post.author_name}</span>
                  <span className="flex items-center gap-1 text-xs text-white/80">
                    <MessageCircle className="w-3 h-3" /> {post.comment_count || 0}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>

        <div className="sm:hidden mt-6 text-center">
          <a href="/blog" className="text-sm font-semibold text-[#bb321a] hover:underline">
            View all posts
          </a>
        </div>
      </div>
    </section>
  );
}
